import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate, useOutletContext, useRouteLoaderData } from 'react-router';
import { EcommerceEventTypeV4 } from '../../common/analytics';
import { paths } from '../../common/constants/pathVariables.js';
import { SubscriptionCategory } from '../../common/enums.js';
import { t } from '../../common/i18n/index.js';
import { getPublishedCatalogs, resolveCategoryUrl } from '../../common/utils/catalogUtils.js';
import { isFinalPage, isNotHavePermissionToBuy } from '../../common/utils/checkoutUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { getEmployeeSubscriptions, loadVirtualCatalogs, resetErrors } from '../../selfservice/actions/index.js';
import { getShoppingCart } from '../../selfservice/common/localStorageUtils.js';
import { ContactsEditProvider } from '../ContactsEditContext/ContactsEditContext.js';
import { DeviceCheckoutDialog } from '../DeviceCheckout/DeviceCheckoutDialogs.js';
import { NotificationContainer } from '../NotificationContainer/NotificationContainer.js';
import { SmallHeader } from '../SmallHeader/SmallHeader.js';
import { SystemError, hasSystemError } from '../SystemError/SystemError.js';
import { EoeCheckoutHeader } from './EoeCheckoutHeader.js';
import { handleCheckoutAnalytics } from './checkoutAnalyticsUtils';
import type { CheckoutRootLoaderData } from '../../common/loaders.js';
import type { ShoppingCartItemForCheckout } from '../../common/types/checkout.js';
import type { State } from '../../selfservice/common/store.js';

import './EoeCheckout.scss';

type CheckoutContext = {
  currentStep: number;
  redirectPath: string;
  setCurrentStep: (step: number) => void;
};

export const useCheckout = () => useOutletContext<CheckoutContext>();

const useRedirectToWebShopWhenCartIsEmpty = ({
  cartItems,
  cartItemsJson,
  redirectPath,
}: {
  cartItems: ShoppingCartItemForCheckout[];
  cartItemsJson: string;
  redirectPath: string;
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    // Prevent automatic redirect to web shop when in thank you page as cart is automatically flushed after successful order
    if (isFinalPage(pathname)) {
      return;
    }

    const rawCartItem: [] = JSON.parse(cartItemsJson);

    if (rawCartItem.length === 0 && cartItems.length === 0) {
      navigate(redirectPath);
    }
  }, [cartItems, cartItemsJson, navigate, redirectPath, pathname]);
};

export const EoeCheckout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItemsJson = getShoppingCart();
  const catalogs = useSelector((s: State) => getPublishedCatalogs(s.selfservice?.virtualCatalogs?.items), deepEqual);
  const selectedCatalogCode = useSelector((s: State) => s.selfservice?.virtualCatalogs?.selectedCatalogCode, deepEqual);
  const categoryUrl = resolveCategoryUrl(catalogs || [], selectedCatalogCode || '');
  const redirectPath = `${paths.EMPLOYEE_DEVICE_STORE}${categoryUrl ? `/${categoryUrl}` : ''}`;

  const { ssoSessionValid, authenticatedUser, validateAuth } = useAuth();

  const { cartItems, errors } = useSelector(
    (state: State) => ({
      cartItems: state.deviceCheckout?.cartItems || [],
      errors: state.deviceCheckout?.errors || authenticatedUser?.errors,
    }),
    deepEqual
  );

  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (ssoSessionValid && isNotHavePermissionToBuy(ssoSessionValid, authenticatedUser)) {
      navigate(redirectPath);
    }
  }, [ssoSessionValid, navigate, redirectPath, authenticatedUser]);

  useRedirectToWebShopWhenCartIsEmpty({
    cartItems,
    cartItemsJson,
    redirectPath,
  });

  // NOTE: this is for public side, to be removed in checkout renewal!
  const loaderData = useRouteLoaderData('checkoutRoot') as CheckoutRootLoaderData | undefined;
  const hasRunOnceRef = useRef(false);

  useEffect(() => {
    if (!hasRunOnceRef.current && cartItems.length) {
      handleCheckoutAnalytics(cartItems, EcommerceEventTypeV4.BEGIN_CHECKOUT, loaderData?.models);
      hasRunOnceRef.current = true;
    }
  }, [cartItems, dispatch, loaderData?.models]);

  useLayoutEffect(() => {
    if (ssoSessionValid) {
      dispatch(loadVirtualCatalogs());
      dispatch(getEmployeeSubscriptions(SubscriptionCategory.DEVICE));
    }
  }, [dispatch, ssoSessionValid, cartItemsJson, cartItems.length, validateAuth]);

  if (hasSystemError(errors)) {
    return (
      <div className="of-checkout">
        <SmallHeader
          buttonText={t.PLAW('Return to the shop')}
          onClick={() => {
            dispatch(resetErrors());
            navigate(redirectPath);
          }}
        />
        <SystemError
          errors={errors}
          onButtonClick={() => {
            dispatch(resetErrors());
            navigate(redirectPath);
          }}
        />
      </div>
    );
  }

  return (
    <div className="of-checkout">
      <EoeCheckoutHeader currentStep={currentStep} redirectPath={redirectPath} />
      <NotificationContainer />
      <ContactsEditProvider>
        <DeviceCheckoutDialog redirectPath={redirectPath} />

        <div className="of-checkout__content">
          <Outlet context={{ currentStep, redirectPath, setCurrentStep }} />
        </div>
      </ContactsEditProvider>
    </div>
  );
};
